import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import ImageField from "./ImageField";
import FocalPointPicker from "./FocalPointPicker";

const inputCls =
  "w-full bg-white border border-[#E5DED4] focus:border-[#9A7B66] rounded-md px-3 py-2 text-sm outline-none transition-colors";

const Label = ({ children }) => (
  <label className="block text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9A7B66] mb-1.5">{children}</label>
);

export default function SettingsTab() {
  const [form, setForm] = useState(null);
  const [boatOperators, setBoatOperators] = useState([]);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setForm(r.data)).catch(() => toast.error("Failed to load settings"));
    api
      .get("/boat-timetable")
      .then((r) => setBoatOperators((r.data?.lembongan_operators || []).map((op) => op.operator)))
      .catch(() => setBoatOperators([]));
  }, []);

  if (!form) return <p className="text-sm text-[#A0988E]">Loading...</p>;

  const set = (k, v) => setForm({ ...form, [k]: v });

  let boatImageMap = {};
  try {
    boatImageMap = JSON.parse(form.boat_company_images || "{}");
  } catch {
    boatImageMap = {};
  }
  const setBoatImage = (operator, url) =>
    set("boat_company_images", JSON.stringify({ ...boatImageMap, [operator]: url }));

  let scheduleOverrides = {};
  try {
    scheduleOverrides = JSON.parse(form.boat_schedule_overrides || "{}");
  } catch {
    scheduleOverrides = {};
  }
  const setScheduleOverride = (operator, direction, value) =>
    set(
      "boat_schedule_overrides",
      JSON.stringify({
        ...scheduleOverrides,
        [operator]: { ...(scheduleOverrides[operator] || {}), [direction]: value },
      })
    );

  const save = async () => {
    try {
      await api.put("/admin/settings", form);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="settings-tab" className="max-w-2xl">
      <h2 className="font-serif text-2xl text-[#2C2825] mb-1">Settings & Booking Engine</h2>
      <p className="text-sm text-[#A0988E] mb-6">
        Booking engine link, brand assets, hero mode, promotion popup, and contact details. SEO, navigation menu and
        links live in the SEO & Links tab.
      </p>

      <div className="bg-white border border-[#E5DED4] rounded-md p-6 space-y-5">
        <div>
          <Label>Booking Engine URL</Label>
          <input
            data-testid="settings-booking-url-input"
            value={form.booking_engine_url || ""}
            onChange={(e) => set("booking_engine_url", e.target.value)}
            placeholder="https://booking.armellabali.com/en/offers?hotel=XXXX"
            className={inputCls}
          />
          <p className="text-xs text-[#A0988E] mt-1.5">
            The Book Now button and the hero reservation widget send guests here with their selected dates, rooms,
            guests and promo code.
          </p>
        </div>

        <div className="border-t border-[#E5DED4] pt-5 space-y-5">
          <div>
            <Label>Logo (format PNG transparan, ditampilkan di latar belakang terang & icon tab)</Label>
            <p className="text-xs text-[#A0988E] mb-2">
              Unggah file logo PNG transparan (bukan foto JPG). Kosongkan jika ingin memakai logo bawaan Nusadira.
            </p>
            <ImageField testId="settings-logo-input" value={form.logo_url} onChange={(v) => set("logo_url", v)} accept="image/png,image/svg+xml,image/webp" />
          </div>
          <div>
            <Label>Logo - Light Version (format PNG transparan putih/krem untuk latar gelap/hero)</Label>
            <p className="text-xs text-[#A0988E] mb-2">
              Unggah file logo PNG putih transparan. Kosongkan jika ingin memakai logo bawaan Nusadira.
            </p>
            <ImageField testId="settings-logo-light-input" value={form.logo_light_url} onChange={(v) => set("logo_light_url", v)} accept="image/png,image/svg+xml,image/webp" />
          </div>
          <div>
            <Label>Logo dengan Subtitle - Footer (PNG transparan, opsional)</Label>
            <p className="text-xs text-[#A0988E] mb-2">
              Versi logo bertuliskan "Lembongan Villa" yang tampil lebih besar di footer. Kosongkan untuk memakai logo utama.
            </p>
            <ImageField testId="settings-logo-footer-input" value={form.logo_footer_url} onChange={(v) => set("logo_footer_url", v)} accept="image/png,image/svg+xml,image/webp" />
          </div>
          <div>
            <Label>Factsheet (PDF) - guests can download it from the Stay section and footer</Label>
            <ImageField testId="settings-factsheet-input" value={form.factsheet_url} onChange={(v) => set("factsheet_url", v)} accept="application/pdf" />
          </div>
        </div>

        <div className="border-t border-[#E5DED4] pt-5 space-y-5">
          <div>
            <Label>Hero Mode</Label>
            <select
              data-testid="settings-hero-mode-select"
              value={form.hero_mode || "carousel"}
              onChange={(e) => set("hero_mode", e.target.value)}
              className={inputCls}
            >
              <option value="carousel">Image carousel (manage slides in Hero Media)</option>
              <option value="video">Single background video</option>
            </select>
          </div>
          {form.hero_mode === "video" && (
            <>
              <div>
                <Label>Hero Video (MP4/WEBM, keep under 20MB for fast loading)</Label>
                <ImageField testId="settings-hero-video-input" value={form.hero_video_url} onChange={(v) => set("hero_video_url", v)} accept="video/mp4,video/webm,video/quicktime" />
              </div>
              <div>
                <Label>Video Poster Image (shown while video loads)</Label>
                <ImageField testId="settings-hero-poster-input" value={form.hero_video_poster} onChange={(v) => set("hero_video_poster", v)} accept="image/*" />
              </div>
            </>
          )}
        </div>

        <div className="border-t border-[#E5DED4] pt-5 space-y-5">
          <label className="flex items-center gap-3 text-sm font-sans font-semibold text-[#2C2825] cursor-pointer">
            <input
              data-testid="settings-promo-enabled"
              type="checkbox"
              checked={form.promo_popup_enabled !== false}
              onChange={(e) => set("promo_popup_enabled", e.target.checked)}
              className="w-4 h-4 accent-[#9A7B66]"
            />
            Show promotion popup on the website
          </label>
          <div>
            <Label>Popup Frequency - seberapa sering popup tampil untuk pengunjung yang sama</Label>
            <select
              data-testid="settings-promo-frequency-select"
              value={form.promo_popup_frequency || "week"}
              onChange={(e) => set("promo_popup_frequency", e.target.value)}
              className={inputCls}
            >
              <option value="always">Setiap buka website</option>
              <option value="session">Sekali per sesi browser</option>
              <option value="day">Sekali per hari</option>
              <option value="3days">Setiap 3 hari</option>
              <option value="week">Seminggu sekali</option>
              <option value="month">Sebulan sekali</option>
            </select>
            <p className="text-xs text-[#A0988E] mt-1.5">
              Setelah tamu menutup popup, popup tidak akan muncul lagi sesuai pilihan ini.
            </p>
          </div>
          <p className="text-xs text-[#A0988E] -mt-2">
            The popup appears gently after guests start scrolling. If fields are empty, guests can browse all currently active Offers with the popup arrows.
          </p>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label>Popup Title</Label>
              <input
                data-testid="settings-promo-title"
                value={form.promo_popup_title || ""}
                onChange={(e) => set("promo_popup_title", e.target.value)}
                placeholder="Stay 4 Nights, Pay for 3"
                className={inputCls}
              />
            </div>
            <div>
              <Label>Promo Code (sent to booking engine)</Label>
              <input
                data-testid="settings-promo-code"
                value={form.promo_popup_promo_code || ""}
                onChange={(e) => set("promo_popup_promo_code", e.target.value)}
                placeholder="NUSADIRA4"
                className={inputCls}
              />
            </div>
            <div>
              <Label>Popup Start Date (optional)</Label>
              <input
                data-testid="settings-promo-start-date"
                type="date"
                value={form.promo_popup_start_date || ""}
                onChange={(e) => set("promo_popup_start_date", e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <Label>Popup End Date (optional)</Label>
              <input
                data-testid="settings-promo-end-date"
                type="date"
                value={form.promo_popup_end_date || ""}
                onChange={(e) => set("promo_popup_end_date", e.target.value)}
                className={inputCls}
              />
            </div>
            <div className="sm:col-span-2">
              <Label>Popup Message</Label>
              <textarea
                data-testid="settings-promo-text"
                rows={3}
                value={form.promo_popup_text || ""}
                onChange={(e) => set("promo_popup_text", e.target.value)}
                placeholder="Linger a little longer above the coral sea..."
                className={`${inputCls} resize-y`}
              />
            </div>
            <div className="sm:col-span-2">
              <Label>Popup Image</Label>
              <ImageField
                testId="settings-promo-image"
                value={form.promo_popup_image || ""}
                onChange={(v) => set("promo_popup_image", v)}
                accept="image/*"
              />
              <FocalPointPicker
                testId="settings-promo-image-focal"
                imageUrl={form.promo_popup_image || ""}
                value={form.promo_popup_image_focal_point || "50% 0%"}
                onChange={(v) => set("promo_popup_image_focal_point", v)}
              />
            </div>
            <div>
              <Label>Popup Image Alt Text (optional)</Label>
              <input
                data-testid="settings-promo-image-alt"
                value={form.promo_popup_image_alt || ""}
                onChange={(e) => set("promo_popup_image_alt", e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <Label>Popup Image SEO Title (optional)</Label>
              <input
                data-testid="settings-promo-image-title"
                value={form.promo_popup_image_title || ""}
                onChange={(e) => set("promo_popup_image_title", e.target.value)}
                className={inputCls}
              />
            </div>
            <div className="sm:col-span-2">
              <Label>Popup Image SEO Description (optional)</Label>
              <textarea
                data-testid="settings-promo-image-description"
                rows={2}
                value={form.promo_popup_image_description || ""}
                onChange={(e) => set("promo_popup_image_description", e.target.value)}
                className={`${inputCls} resize-y`}
              />
            </div>
            <div>
              <Label>Popup Image Caption (optional)</Label>
              <input
                data-testid="settings-promo-image-caption"
                value={form.promo_popup_image_caption || ""}
                onChange={(e) => set("promo_popup_image_caption", e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <Label>Booking Button Label</Label>
              <input
                data-testid="settings-promo-cta-label"
                value={form.promo_popup_cta_label || ""}
                onChange={(e) => set("promo_popup_cta_label", e.target.value)}
                placeholder="Book This Offer"
                className={inputCls}
              />
            </div>
            <div>
              <Label>Booking Button URL (optional override)</Label>
              <input
                data-testid="settings-promo-cta-url"
                value={form.promo_popup_cta_url || ""}
                onChange={(e) => set("promo_popup_cta_url", e.target.value)}
                placeholder="Uses Booking Engine URL when empty"
                className={inputCls}
              />
            </div>
          </div>
        </div>

        <div className="border-t border-[#E5DED4] pt-5 grid sm:grid-cols-2 gap-5">
          <div>
            <Label>Contact Email</Label>
            <input data-testid="settings-email-input" value={form.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className={inputCls} />
          </div>
          <div>
            <Label>Contact Phone / WhatsApp</Label>
            <input data-testid="settings-phone-input" value={form.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} className={inputCls} />
          </div>
          <div className="sm:col-span-2">
            <Label>Address</Label>
            <input data-testid="settings-address-input" value={form.contact_address || ""} onChange={(e) => set("contact_address", e.target.value)} className={inputCls} />
          </div>
          <div className="sm:col-span-2">
            <Label>Google Maps / Business Profile URL</Label>
            <input data-testid="settings-google-maps-input" value={form.google_maps_url || ""} onChange={(e) => set("google_maps_url", e.target.value)} className={inputCls} placeholder="https://share.google/..." />
            <p className="text-xs text-[#A0988E] mt-1.5">Link Google Business Profile villa - tombol "View on Google Maps" tampil di section Contact saat terisi.</p>
          </div>
        </div>

        <div className="border-t border-[#E5DED4] pt-5 space-y-4">
          <div>
            <Label>Boat Company Photos - tampil di filter Boat Schedule</Label>
            <p className="text-xs text-[#A0988E] mb-3">
              Upload foto boat untuk setiap perusahaan. Foto tampil otomatis saat tamu memilih perusahaan di filter jadwal.
            </p>
            {boatOperators.length === 0 ? (
              <p className="text-xs text-[#A0988E]">Nama perusahaan boat dimuat dari jadwal… coba buka lagi sebentar lagi.</p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4" data-testid="boat-images-manager">
                {boatOperators.map((operator) => (
                  <div key={operator} className="rounded-md border border-[#E5DED4] bg-[#FBF9F5] p-3">
                    <p className="text-[11px] font-sans font-semibold text-[#2C2825] mb-2">{operator}</p>
                    <ImageField
                      testId={`boat-image-${operator.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      value={boatImageMap[operator] || ""}
                      onChange={(url) => setBoatImage(operator, url)}
                      accept="image/*"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-[#E5DED4] pt-5 space-y-4">
          <div>
            <Label>Boat Schedule Override - isi jam manual jika jadwal dari link sumber belum update</Label>
            <p className="text-xs text-[#A0988E] mb-3">
              Format: jam dipisah koma, contoh 07:30, 10:00, 13:00. Kosongkan agar jadwal mengikuti link sumber. Jadwal yang
              di-override diberi tanda "Updated by villa" di website.
            </p>
            {boatOperators.length === 0 ? (
              <p className="text-xs text-[#A0988E]">Nama perusahaan boat dimuat dari jadwal… coba buka lagi sebentar lagi.</p>
            ) : (
              <div className="space-y-3" data-testid="boat-schedule-overrides">
                {boatOperators.map((operator) => (
                  <div key={operator} className="rounded-md border border-[#E5DED4] bg-[#FBF9F5] p-3 grid sm:grid-cols-[180px_1fr_1fr] gap-3 items-center">
                    <p className="text-[11px] font-sans font-semibold text-[#2C2825]">{operator}</p>
                    <input
                      data-testid={`boat-override-outbound-${operator.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      value={scheduleOverrides[operator]?.sanur || ""}
                      onChange={(e) => setScheduleOverride(operator, "sanur", e.target.value)}
                      placeholder="Sanur → Lembongan"
                      className={inputCls}
                    />
                    <input
                      data-testid={`boat-override-inbound-${operator.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      value={scheduleOverrides[operator]?.lembongan || ""}
                      onChange={(e) => setScheduleOverride(operator, "lembongan", e.target.value)}
                      placeholder="Lembongan → Sanur"
                      className={inputCls}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-[#E5DED4] pt-5 space-y-4">
          <div>
            <Label>Instagram Feed - opsional, untuk menampilkan foto terbaru dari akun IG</Label>
            <p className="text-xs text-[#A0988E] mb-3">
              Isi Long-Lived Access Token dan Account ID dari Meta for Developers (akun IG harus Business/Creator). Jika
              kosong, section Instagram menampilkan foto galeri. Token berlaku 60 hari.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <input
                data-testid="settings-instagram-token-input"
                type="password"
                value={form.instagram_access_token || ""}
                onChange={(e) => set("instagram_access_token", e.target.value)}
                placeholder="Instagram Access Token"
                className={inputCls}
              />
              <input
                data-testid="settings-instagram-account-input"
                value={form.instagram_account_id || ""}
                onChange={(e) => set("instagram_account_id", e.target.value)}
                placeholder="Instagram Account ID"
                className={inputCls}
              />
            </div>
          </div>
        </div>

        <button
          data-testid="settings-save-button"
          onClick={save}
          className="px-8 py-3 rounded-sm bg-[#9A7B66] hover:bg-[#826552] text-white text-xs font-semibold uppercase tracking-wider transition-colors"
        >
          Save Settings
        </button>
      </div>
    </div>
  );
}
